/**
 * FlagNoteDialog - Add / edit / clear a dispatcher's "needs attention" note on
 * an order from the map. The note IS the flag: any text flags the order (red
 * ring on its marker); clearing the text removes the flag.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flag } from 'lucide-react';

const FlagNoteDialog = ({ order, open, scope, blocked = false, onOpenChange, onSave, recovery, onCheckCurrent, onAcknowledge, onRefresh }) => {
  const epoch = useMemo(() => ({ id: order?.id, open, scope }), [order?.id, open, scope]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const initial = { epoch, text: order?.dispatch_note || '', dirty: false, revision: 0 };
  const visible = draft.epoch === epoch ? draft : initial;
  const latest = useRef(); latest.current = { epoch, open, draft: visible };
  const pending = useRef(null);
  useEffect(() => {
    setDraft(previous => previous.epoch === epoch && previous.dirty ? previous
      : { epoch, text: order?.dispatch_note || '', dirty: false, revision: 0 });
  }, [epoch, order?.dispatch_note]);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  if (!order) return null;
  const recipient = order.recipient_name || 'this order';
  const handleSave = async () => {
    if (!open || blocked || pending.current === epoch) return;
    const revision = visible.revision;
    const current = () => mounted.current && latest.current?.epoch === epoch && latest.current.open;
    pending.current = epoch; setSaving(epoch); setFeedback(null);
    try {
      const result = await onSave(order.id, visible.text.trim(), current);
      if (!current()) return;
      if (result?.state === 'accepted') {
        if (latest.current.draft.revision === revision) onOpenChange(false);
        else setFeedback({ epoch, message: 'Earlier text saved. Your newer edits are still here; save them when ready.' });
      } else if (result?.state !== 'retired') {
        setFeedback({ epoch, message: result?.message || 'Save not confirmed. Your draft is retained.' });
      }
    } catch {
      if (current()) setFeedback({ epoch, message: 'Save not confirmed. Your draft is retained.' });
    } finally {
      if (pending.current === epoch) pending.current = null;
      if (current()) setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-600" />
            Flag for attention
          </DialogTitle>
          <DialogDescription>
            Note a special circumstance for {recipient}. Anything you type flags this
            order on the map; clear the text to remove the flag.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <textarea
            className="w-full min-h-[96px] rounded-xl border border-stone-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
            placeholder="e.g. Must be delivered by noon — recipient leaves for work"
            value={visible.text}
            onChange={(e) => {
              const next = { epoch, text: e.target.value, dirty: true, revision: latest.current.draft.revision + 1 };
              latest.current.draft = next; setDraft(next);
            }}
            autoFocus
            data-testid="flag-note-input"
          />
        </div>
        {feedback?.epoch === epoch && <p role="status">{feedback.message}</p>}
        {recovery && <div role="status" className="space-y-2 text-sm">
          <p>{recovery.state === 'pending' ? recovery.stalled ? 'Still awaiting the save response. Do not repeat it.' : 'The submitted save continues if you close this dialog.' : recovery.message}</p>
          {recovery.state === 'accepted' && <Button variant="outline" onClick={onRefresh}>Refresh orders</Button>}
          {recovery.state === 'unknown' && <Button variant="outline" onClick={onCheckCurrent}>Check current order</Button>}
          {recovery.review && <p>{recovery.review}</p>}
          {recovery.canAcknowledge && <Button variant="outline" onClick={onAcknowledge}>Acknowledge and refresh</Button>}
        </div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{saving === epoch ? 'Close' : 'Cancel'}</Button>
          <Button onClick={handleSave} disabled={saving === epoch || blocked} data-testid="flag-note-save">
            {saving === epoch ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlagNoteDialog;
